import { AGENT_TRACKER_ROLE, ENGAGEMENT_TYPES } from "vue-app/types/enums";

const getStartedPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/engagements/${engagementName}`,
  redirect: `/app/ready-flows/${engagementName}`,
  component: () => import("../twig/TwigPageContainer"),
  props: () => ({
    pageTitle: "GET STARTED",
    pageDesc: "See how this scenario can help you attract customers",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const adjustPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/engagements/${engagementName}/adjust`,
  redirect: `/app/ready-flows/${engagementName}/adjust`,
  component: () => import("../twig/TwigPageContainer"),
  props: () => ({
    pageTitle: "Adjust",
    pageDesc: "Customize your campaign to boost overall conversion",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/parameters`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const languagePageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/engagements/${engagementName}/language`,
  redirect: `/app/ready-flows/${engagementName}/language`,
  component: () => import("../twig/TwigPageContainer"),
  props: () => ({
    pageTitle: "Language",
    pageDesc:
      "eCommerce is global and your customers are everywhere. Language options allow you to speak with them in the most familiar way possible.",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/language`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const senderPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/engagements/${engagementName}/sender`,
  redirect: `/app/ready-flows/${engagementName}/sender`,
  component: () => import("../twig/TwigPageContainer"),
  props: () => ({
    pageTitle: "Sender",
    pageDesc: "Use the ability to change sender names to best suit the kind of communication you are sharing.",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/sender`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const designPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/engagements/${engagementName}/design`,
  redirect: `/app/ready-flows/${engagementName}/design`,
  component: () => import("./design/EngagementDesignPage"),
  props: () => ({
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/design`,
    engagementName,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const runPageFactory = ({ engagementType, engagementName, routeName }) => ({
  path: `/app/engagements/${engagementName}/run`,
  redirect: `/app/ready-flows/${engagementName}/run`,
  component: () => import("../twig/TwigPageContainer"),
  props: () => ({
    pageTitle: "Run",
    pageDesc: "Allow agents to check for bugs and run scenarios.",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/run`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const reportsPageFactory = ({ engagementType, engagementName, isTransactional, routeName }) => ({
  path: `/app/engagements/${engagementName}/reports`,
  redirect: `/app/ready-flows/${engagementName}/reports`,
  component: () => import("./reports/EngagementReport"),
  props: () => ({
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    skipGlobalLoader: true,
    engagementTypeId: engagementType,
    isGlobalNewslettersReport: false,
    isTransactionalEngagement: isTransactional,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
  },
});

const engagementBlockedPageFactory = ({ engagementType, engagementName, pageName, pageTitle, routeName }) => ({
  path: `/app/engagements/${engagementName}/${pageName}`,
  redirect: `/app/ready-flows/${engagementName}/${pageName}`,
  component: () => import("../twig/TwigPageContainer"),
  props: () => ({
    pageTitle,
    pageDesc: "Make sure that engagement configuration is correct",
    pageHelper: { name: engagementName },
  }),
  meta: {
    routeName,
    legacyLayout: true,
    legacyHref: `/${engagementName}/${pageName}`,
    engagementTypeId: engagementType,
    requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
    noPadding: true,
    skipGlobalLoader: true,
  },
});

const referAFriend = [
  {
    path: "/app/engagements/refer-a-friend",
    redirect: "/app/ready-flows/refer-a-friend",
    component: () => import("vue-app/pages/PageReadyFlowLegacy.vue"),
    props: () => ({
      engagementName: "refer-a-friend",
      engagementTypeId: 20,
    }),
    children: [
      {
        path: "/app/engagements/refer-a-friend",
        component: () => import("./referAFriend/ReferAFriendGetStarted"),
        props: () => ({
          pageHelper: { name: "refer-a-friend" },
        }),
        meta: {
          routeName: window.$_t("REFER-A-FRIEND"),
          legacyLayout: true,
          engagementTypeId: ENGAGEMENT_TYPES.REFER_A_FRIEND,
          noPadding: true,
          skipGlobalLoader: true,
        },
      },
      {
        path: "/app/engagements/refer-a-friend/opt-in",
        component: () => import("./referAFriend/ReferAFriendOptIn"),
        props: () => ({
          pageHelper: { name: "refer-a-friend" },
        }),
        meta: {
          routeName: window.$_t("REFER-A-FRIEND"),
          legacyLayout: true,
          engagementTypeId: ENGAGEMENT_TYPES.REFER_A_FRIEND,
          noPadding: true,
          skipGlobalLoader: true,
        },
      },
      adjustPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      languagePageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      senderPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      designPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      {
        path: "/app/engagements/refer-a-friend/popup",
        component: () => import("vue-app/pages/PageOnsitePopups/PageOnsitePopups"),
        props: () => ({
          showHeader: false,
        }),
        meta: {
          routeName: window.$_t("REFER-A-FRIEND"),
          engagementTypeId: ENGAGEMENT_TYPES.REFER_A_FRIEND,
          skipGlobalLoader: true,
          noPadding: true,
        },
      },
      runPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
      }),
      reportsPageFactory({
        routeName: window.$_t("REFER-A-FRIEND"),
        engagementName: "refer-a-friend",
        engagementType: ENGAGEMENT_TYPES.REFER_A_FRIEND,
        isTransactional: true,
      }),
    ],
  },
];

const chatSubscription = [
  {
    path: "/app/engagements/chat-subscription",
    redirect: "/app/ready-flows/chat-subscription",
    component: () => import("vue-app/pages/PageReadyFlowLegacy.vue"),
    props: () => ({
      engagementName: "chat-subscription",
      engagementTypeId: 18,
    }),
    children: [
      getStartedPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      adjustPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      languagePageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      senderPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      designPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      runPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
      }),
      reportsPageFactory({
        routeName: window.$_t("CHAT SUBSCRIPTION"),
        engagementName: "chat-subscription",
        engagementType: ENGAGEMENT_TYPES.CHAT_SUBSCRIPTION,
        isTransactional: true,
      }),
    ],
  },
];

const crossSellingTransactional = [
  {
    path: "/app/engagements/cross-selling-transactional",
    redirect: "/app/ready-flows/cross-selling-transactional",
    component: () => import("vue-app/pages/PageReadyFlowLegacy.vue"),
    props: () => ({
      engagementName: "cross-selling-transactional",
      engagementTypeId: 16,
    }),
    children: [
      getStartedPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      {
        path: `/app/engagements/cross-selling-transactional/adjust`,
        component: () => import("./cross-selling/CrossSellingAdjust"),
        meta: {
          routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
          legacyLayout: true,
          engagementTypeId: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
          requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
          noPadding: true,
          skipGlobalLoader: true,
        },
      },
      languagePageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      senderPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      designPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      runPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
      }),
      reportsPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
        isTransactional: true,
      }),
      engagementBlockedPageFactory({
        routeName: window.$_t("CROSS_SELLING_TRANSACTIONAL"),
        engagementName: "cross-selling-transactional",
        engagementType: ENGAGEMENT_TYPES.CROSS_SELLING_TRANSACTIONAL,
        pageTitle: "Design",
        pageName: "missing-rules",
      }),
    ],
  },
];

const oldEngagementRoutes = [
  {
    path: "/app/engagements/after-sale",
    redirect: "/app/ready-flows/after-sale",
  },
  {
    path: "/app/engagements/advanced-restore-customers",
    redirect: "/app/ready-flows/advanced-restore-customers",
  },
  {
    path: "/app/engagements/restore-customers",
    redirect: "/app/ready-flows/restore-customers",
  },
  { path: "/app/engagements/loyalty-programme", redirect: "/app/ready-flows/loyalty-program" },
  { path: "/app/ready-flows/loyalty-programme", redirect: "/app/ready-flows/loyalty-program" },
  {
    path: "/app/engagements/loyalty-program",
    redirect: "/app/ready-flows/loyalty-program",
  },
  {
    path: "/app/engagements/recover-carts",
    redirect: "/app/ready-flows/recover-carts",
  },
  {
    path: "/app/engagements/sms-subscription",
    redirect: "/app/ready-flows/sms-subscription",
  },
  {
    path: "/app/engagements/newsletter-signup-after-purchase",
    redirect: "/app/ready-flows/newsletter-signup-after-purchase",
  },
  {
    path: "/app/engagements/price-change-alert-up",
    redirect: "/app/ready-flows/price-change-alert-up",
  },
  {
    path: "/app/engagements/price-change-alert-down",
    redirect: "/app/ready-flows/price-change-alert-down",
  },
  {
    path: "/app/engagements/newest-products",
    redirect: "/app/ready-flows/newest-products",
  },
  {
    path: "/app/engagements/low-stock-products",
    redirect: "/app/ready-flows/low-stock-products",
  },
  {
    path: "/app/engagements/ask-for-review",
    redirect: "/app/ready-flows/ask-for-review",
  },
  {
    path: "/app/engagements/back-in-stock",
    redirect: "/app/ready-flows/back-in-stock",
  },
  {
    path: "/app/engagements/newsletter-subscription",
    redirect: "/app/ready-flows/newsletter-subscription",
  },
];

export const engagementRoutes = [
  {
    path: "/engagements",
    redirect: "/app/engagements",
  },
  {
    path: "/app/engagements",
    redirect: "/app/ready-flows",
    component: () => import("../../../../vue-app/pages/PageReadyFlows/PageReadyFlows.vue"),
    meta: {
      routeName: window.$_t("ENGAGEMENTS"),
      requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
      noPadding: true,
    },
  },
  {
    path: "/app/engagements/attributions",
    redirect: "/app/ready-flows/attributions",
    props: (route) => ({
      firstDate: route.query ? route.query.firstDate : null,
      lastDate: route.query ? route.query.lastDate : null,
      engagementId: route.query ? route.query.engagementId : null,
      engagementTypeId: route.query ? route.query.engagementTypeId : null,
      isSummary: Boolean(route.query && route.query.engagementTypeId),
    }),
    component: () => import("./attributions/EngagementAttributions"),
    meta: {
      routeName: window.$_t("Attributions"),
      legacyLayout: true,
      requiredRoles: [AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT],
      skipGlobalLoader: true,
    },
  },
  ...referAFriend,
  ...crossSellingTransactional,
  ...chatSubscription,
  ...oldEngagementRoutes,
];
